import type { JobsiteBusinessHours } from "../types/runtime-config";

const MINUTES_PER_HOUR = 60,
  HOURS_PER_DAY = 24,
  NOON = 12,
  /** The hours an "open all day" week is stored as. */
  ALL_DAY = { closes: "23:59", opens: "00:00" },
  /** The week as the API lists it, with the short names people write on a door. */
  WEEK = [
    { day: "Monday", short: "Mon" },
    { day: "Tuesday", short: "Tue" },
    { day: "Wednesday", short: "Wed" },
    { day: "Thursday", short: "Thu" },
    { day: "Friday", short: "Fri" },
    { day: "Saturday", short: "Sat" },
    { day: "Sunday", short: "Sun" },
  ],

  /**
   * "07:00" → "7am", "17:30" → "5:30pm", "12:00" → "12pm".
   * @param {string} value A 24-hour "HH:MM" time.
   * @returns {string | null} The time as a customer says it, or null when it is not a time.
   */
  formatClock = (value: string): string | null => {
    const match = /^(?<hours>\d{1,2}):(?<minutes>\d{2})$/u.exec(value.trim()),
      hours = Number(match?.groups?.hours),
      minutes = Number(match?.groups?.minutes);
    if (!match || hours >= HOURS_PER_DAY || minutes >= MINUTES_PER_HOUR) {
      return null;
    }
    const suffix = hours < NOON ? "am" : "pm",
      clockHour = hours % NOON === 0 ? NOON : hours % NOON;
    if (minutes === 0) {
      return `${clockHour}${suffix}`;
    }
    return `${clockHour}:${String(minutes).padStart(2, "0")}${suffix}`;
  },

  /**
   * The weekday it is now where the business is ("Monday"). An unknown or
   * invalid zone falls back to the visitor's own clock, which is the business's
   * for nearly every visitor a local business gets.
   * @param {Date} now The current moment.
   * @param {string | null | undefined} timezone The business's IANA zone.
   * @returns {string} The English weekday name.
   */
  weekdayAt = (now: Date, timezone: string | null | undefined): string => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: timezone ?? undefined, weekday: "long" }).format(now);
    } catch {
      return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(now);
    }
  },

  /**
   * Whether a day is stored as open all day.
   * @param {JobsiteBusinessHours} day The day.
   * @returns {boolean} True for midnight to 11:59pm.
   */
  isAllDay = (day: JobsiteBusinessHours): boolean => day.opens === ALL_DAY.opens && day.closes === ALL_DAY.closes,

  /**
   * An open day's hours as a sentence: "Open today, 7am to 5pm" or "Open 24 hours today".
   * @param {JobsiteBusinessHours} today The day it is.
   * @returns {string} The line.
   */
  openTodayText = (today: JobsiteBusinessHours): string => {
    if (isAllDay(today)) {
      return "Open 24 hours today";
    }
    const opens = formatClock(today.opens),
      closes = formatClock(today.closes);
    if (opens === null || closes === null) {
      return "Open today";
    }
    return `Open today, ${opens} to ${closes}`;
  },

  /**
   * Today's hours as one line: "Open today, 7am to 5pm", "Open 24 hours today" or "Closed today".
   * @param {JobsiteBusinessHours[] | undefined} hours The days the business is open.
   * @param {Date} now The current moment.
   * @param {string | null | undefined} timezone The business's IANA zone.
   * @returns {string | null} The line, or null when the owner never set hours.
   */
  todayHoursText = (
    hours: JobsiteBusinessHours[] | undefined,
    now: Date,
    timezone?: string | null,
  ): string | null => {
    if (!hours || hours.length === 0) {
      return null;
    }
    const weekday = weekdayAt(now, timezone),
      today = hours.find((day) => day.day === weekday);
    return today ? openTodayText(today) : "Closed today";
  },

  /**
   * One day's hours as written on a door: "7am–5pm", or "24 hours".
   * @param {JobsiteBusinessHours} day The day.
   * @returns {string} The hours.
   */
  dayHoursText = (day: JobsiteBusinessHours): string => {
    if (isAllDay(day)) {
      return "24 hours";
    }
    return `${formatClock(day.opens) ?? day.opens}–${formatClock(day.closes) ?? day.closes}`;
  },

  /**
   * The whole week in one line, runs of days with the same hours joined:
   * "Mon–Fri 7am–5pm, Sat 8am–12pm". It does not depend on the day it is read,
   * so the server can render it.
   * @param {JobsiteBusinessHours[]} [hours] The days the business is open.
   * @returns {string} The week, or "" when the owner never set hours.
   */
  weekHoursText = (hours?: JobsiteBusinessHours[]): string => {
    const runs: { first: string; last: string; text: string; lastIndex: number }[] = [];
    for (const [index, { day, short }] of WEEK.entries()) {
      // Not runs.at(-1): Safari before 15.4 has no Array.prototype.at, and this renders on every bold hero.
      const open = hours?.find((entry) => entry.day === day),
        text = open ? dayHoursText(open) : null,
        [run] = runs.slice(-1);
      if (text !== null && run?.text === text && run.lastIndex === index - 1) {
        run.last = short;
        run.lastIndex = index;
      } else if (text !== null) {
        runs.push({ first: short, last: short, lastIndex: index, text });
      }
    }
    return runs
      .map((run) => `${run.first === run.last ? run.first : `${run.first}–${run.last}`} ${run.text}`)
      .join(", ");
  };

export { formatClock, todayHoursText, weekHoursText };
